import React from 'react'
import * as LC from './lazyComponents'
import { createCheckoutPageConfig } from './checkoutPageConfig'
import { type MainLayoutCustomStylesWithStatesProps } from '@applyft-web/ui-components'
import { LocalePathGate } from './LocalePathGate'
import type { InputAttention, SearchTabId } from '@/pages/Welcome/types'

export interface CommonProps {
  readonly withButton?: boolean
  readonly headerProps?: {
    withHeader: boolean
    defaultState?: boolean
  }
  readonly withFooter?: boolean
  readonly withoutWrap?: boolean
  readonly withBackButton?: boolean
  readonly wrapperProps?: {
    adaptive?: boolean
    pt?: number
    pb?: number
    customStyles?: MainLayoutCustomStylesWithStatesProps
  }
  readonly withProgressBar?: boolean
  readonly insideGroup?: boolean
}

export interface ConfigProps extends CommonProps {
  readonly component: LC.LazyComponentWithPreload<React.ComponentType<any>>
  readonly images?: string[]
  readonly componentProps?: Record<string, any>
}

/**
 * The `*_kw` variants are byte-identical to their counterparts — they exist so
 * the A/B test can tell the arms apart by screen id. Which H1 they show is
 * decided at runtime from the ad's `utm_term` (see `resolveKeywordTitleKey`).
 */
function createLookupPageConfig(
  initTab?: 'email' | 'name' | 'photo',
  inputAttention?: InputAttention,
): ConfigProps {
  const componentProps = {
    ...(initTab ? { initTab } : {}),
    ...(inputAttention ? { inputAttention } : {}),
  }

  return {
    component: LC.Welcome,
    ...(Object.keys(componentProps).length ? { componentProps } : {}),
    withButton: false,
    headerProps: {
      withHeader: true,
    },
    withFooter: true,
    wrapperProps: {
      customStyles: {
        default: 'overflow-x:hidden;text-align:inherit;',
      },
    },
  }
}
function createDefaultLookupPageConfig(initTab: SearchTabId = 'phone_number'): ConfigProps {
  return {
    component: LC.Welcome,
    componentProps: {
      initTab,
      defaultState: true,
    },
    withButton: false,
    headerProps: {
      withHeader: true,
      defaultState: true,
    },
    withFooter: true,
    wrapperProps: {
      customStyles: {
        default: 'overflow-x:hidden;text-align:inherit;',
      },
    },
  }
}

export const pagesConfig: { [key: string]: ConfigProps } = {
  phone_lookup_default: createDefaultLookupPageConfig(),
  email_lookup_default: createDefaultLookupPageConfig('email'),
  name_lookup_default: createDefaultLookupPageConfig('name'),
  photo_lookup_default: createDefaultLookupPageConfig('photo'),
  phone_lookup: createLookupPageConfig(),
  email_lookup: createLookupPageConfig('email'),
  name_lookup: createLookupPageConfig('name'),
  photo_lookup: createLookupPageConfig('photo'),
  phone_lookup_kw: createLookupPageConfig(),
  email_lookup_kw: createLookupPageConfig('email'),
  name_lookup_kw: createLookupPageConfig('name'),
  // First-screen input attention A/B: animated border / error-like state / glow.
  phone_lookup_an: createLookupPageConfig(undefined, 'animation'),
  email_lookup_an: createLookupPageConfig('email', 'animation'),
  name_lookup_an: createLookupPageConfig('name', 'animation'),
  phone_lookup_er: createLookupPageConfig(undefined, 'error'),
  email_lookup_er: createLookupPageConfig('email', 'error'),
  name_lookup_er: createLookupPageConfig('name', 'error'),
  phone_lookup_gl: createLookupPageConfig(undefined, 'glow'),
  email_lookup_gl: createLookupPageConfig('email', 'glow'),
  name_lookup_gl: createLookupPageConfig('name', 'glow'),
  lookup: {
    component: LC.Lookup,
    withButton: false,
  },
  lookup_default: {
    component: LC.Lookup,
    componentProps: {
      defaultState: true,
    },
    withButton: false,
  },
  email_consent: {
    component: LC.EmailConsent,
    withButton: false,
    withoutWrap: true,
  },
  paycheck1_pr1_v_3_r: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_3_r',
    ftcPaywall: true,
    blockPrepaidCards: true,
    withRevolutPay: true,
    preferRevolutPayTab: true,
  }),
  paycheck1_pr1_v_7tr_r: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_7tr_r',
    paywallType: '7day_trial',
    blockPrepaidCards: true,
    preferRevolutPayTab: true,
    withRevolutPay: true,
  }),
  paycheck1_pr1_v_r: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_r',
    blockPrepaidCards: true,
    withRevolutPay: true,
    preferRevolutPayTab: true,
  }),
  paycheck1_pr1_v: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v',
    isShowPolandPlans: true,
    isShowBrazilPlans: true,
  }),
  paycheck1_pr1_v_7tr: createCheckoutPageConfig({
    paywallType: '7day_trial',
  }),
  paycheck1_pr1_v_2: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_2',
    blockPrepaidCards: true,
  }),
  paycheck1_pr1_v_p: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_p',
    blockPrepaidCards: true,
    isShowPixAutomatico: true,
    isShowBrazilPlans: true,
  }),
  crm__pr1_v_2: createCheckoutPageConfig({
    screenId: 'crm__pr1_v_2',
    blockPrepaidCards: true,
    useAuth: true,
  }),
  paycheck1_pr1_v_2_consent: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_2_consent',
    blockPrepaidCards: true,
    withEmailConsent: true,
    emailConsentEventId: 'paycheck1_pr1_v_2_email_consent',
  }),
  paycheck1_pr1_v_4: createCheckoutPageConfig({
    blockPrepaidCards: true,
  }),
  // FTC-compliant WW rollout (all geos/sources): google → v_5, meta → v_7tr_3.
  // Unlike v_3, the trial is fixed per screen and the geo swap skips them.
  paycheck1_pr1_v_5: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_5',
    ftcPaywall: true,
    ftcTrialDays: 1,
    blockPrepaidCards: true,
  }),
  paycheck1_pr1_v_7tr_3: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_7tr_3',
    ftcPaywall: true,
    ftcTrialDays: 7,
    blockPrepaidCards: true,
  }),
  paycheck1_pr1_default: {
    ...createCheckoutPageConfig({
      blockPrepaidCards: true,
    }),
    component: LC.Paycheck1Pr1Default,
  },
  paycheck1_pr1_v_6: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_6',
    ftcPaywall: true,
    ftcTrialDays: 1,
    blockPrepaidCards: true,
    hidePaypal: true,
  }),
  paycheck1_pr1_v_7tr_6: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_7tr_6',
    ftcPaywall: true,
    ftcTrialDays: 7,
    blockPrepaidCards: true,
    hidePaypal: true,
  }),
  paycheck1_pr1_v_b: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_b',
    blockPrepaidCards: true,
    isShowBlik: true,
    isShowPolandPlans: true,
  }),
  paycheck1_pr1_v_7tr_b: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_7tr_b',
    paywallType: '7day_trial',
    blockPrepaidCards: true,
    isShowBlik: true,
    isShowPolandPlans: true,
  }),
  paycheck1_pr1_v_a: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_a',
    ftcPaywall: true,
    ftcTrialDays: 1,
    blockPrepaidCards: true,
    withAmazonPay: true,
  }),
  paycheck1_pr1_v_7tr_a: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_7tr_a',
    ftcPaywall: true,
    ftcTrialDays: 7,
    blockPrepaidCards: true,
    withAmazonPay: true,
  }),
  paycheck1_pr1_v_7tr_2: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_7tr_2',
    paywallType: '7day_trial',
    blockPrepaidCards: true,
  }),
  paycheck1_pr1_v_7tr_p: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_7tr_p',
    paywallType: '7day_trial',
    blockPrepaidCards: true,
    isShowPixAutomatico: true,
    isShowBrazilPlans: true,
  }),
  paycheck1_pr1_v_7tr_2_consent: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_7tr_2_consent',
    paywallType: '7day_trial',
    blockPrepaidCards: true,
    withEmailConsent: true,
    emailConsentEventId: 'paycheck1_pr1_v_7tr_2_email_consent',
  }),
  paycheck1_pr1_v_7tr_4: createCheckoutPageConfig({
    paywallType: '7day_trial',
    blockPrepaidCards: true,
  }),
  paycheck1_pr1_v_3: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_3',
    ftcPaywall: true,
    blockPrepaidCards: true,
    isShowPolandPlans: true,
    isShowBrazilPlans: true,
  }),
  paycheck1_pr1_v_3_b: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_3_b',
    ftcPaywall: true,
    blockPrepaidCards: true,
    isShowPolandPlans: true,
    isShowBlik: true,
    isBlikFirst: true,
  }),
  paycheck1_pr1_v_3_p: createCheckoutPageConfig({
    screenId: 'paycheck1_pr1_v_3_p',
    ftcPaywall: true,
    blockPrepaidCards: true,
    isShowBrazilPlans: true,
    isShowPixAutomatico: true,
  }),
  success: {
    component: LC.Success,
    images: [],
    withButton: false,
    wrapperProps: {
      customStyles: {
        default: 'max-width: 640px; text-align: inherit;',
      },
      pt: 0,
    },
  },
  success_default: {
    component: LC.Success,
    componentProps: { defaultState: true },
    images: [],
    withButton: false,
    wrapperProps: {
      customStyles: {
        default: 'max-width: 640px; text-align: inherit;',
      },
      pt: 0,
    },
  },
  'terms-of-use': {
    component: LC.Terms,
    componentProps: {
      screenId: 'terms',
    },
    withoutWrap: true,
  },
  'privacy-policy': {
    component: LC.Policy,
    componentProps: {
      screenId: 'policy',
    },
    withoutWrap: true,
  },
  '*': {
    component: LC.EntryPoint,
    withButton: false,
    withoutWrap: true,
  },
}

export interface RoutesProps extends CommonProps {
  readonly path: string
  readonly element: React.JSX.Element
}

function buildPageRoute(path: string, conf: ConfigProps): RoutesProps {
  const { component: Comp, componentProps, withButton = true, withoutWrap = false } = conf
  const screenId = componentProps?.screenId ?? path

  return {
    ...conf,
    path: `/${path}`,
    element: <Comp {...componentProps} screenId={screenId} />,
    withButton,
    withoutWrap,
  }
}

export const pagesRoutes: RoutesProps[] = Object.entries(pagesConfig).flatMap(([path, conf]) => {
  const base = buildPageRoute(path, conf)
  if (path === '*') return [base]

  return [
    base,
    {
      ...base,
      path: `/:locale${base.path}`,
      element: <LocalePathGate>{base.element}</LocalePathGate>,
    },
  ]
})

interface GroupLayout {
  readonly component: LC.LazyComponentWithPreload<React.ComponentType<any>>
  readonly props?: Record<string, any>
}

export interface GroupConfigProps {
  readonly layout: GroupLayout
  readonly children: { [key: string]: ConfigProps }
}

export const pageGroupsConfig: GroupConfigProps[] = [
  {
    layout: {
      component: LC.Map,
    },
    children: {
      searching: {
        component: LC.Searching,
        withoutWrap: true,
      },
      warning: {
        component: LC.Warning,
        withoutWrap: true,
      },
      validating: {
        component: LC.Validating,
        withoutWrap: true,
      },
    },
  },
  {
    layout: {
      component: LC.Map,
      props: {
        defaultState: true,
      },
    },
    children: {
      searching_default: {
        component: LC.Searching,
        componentProps: {
          defaultState: true,
        },
        withoutWrap: true,
      },
      warning_default: {
        component: LC.Warning,
        componentProps: {
          defaultState: true,
        },
        withoutWrap: true,
      },
      validating_default: {
        component: LC.Validating,
        componentProps: {
          defaultState: true,
        },
        withoutWrap: true,
      },
    },
  },
  {
    layout: {
      component: LC.Result,
    },
    children: {
      report_ready: {
        component: LC.ReportReady,
        withoutWrap: true,
      },
      signup: {
        component: LC.Signup,
        withoutWrap: true,
      },
      signup_consent: {
        component: LC.Signup,
        componentProps: { withEmailConsent: true },
        withoutWrap: true,
      },
    },
  },
  {
    layout: {
      component: LC.Result,
      props: {
        defaultState: true,
      },
    },
    children: {
      report_ready_default: {
        component: LC.ReportReady,
        componentProps: {
          defaultState: true,
        },
        withoutWrap: true,
      },
      signup_default: {
        component: LC.Signup,
        componentProps: {
          defaultState: true,
        },
        withoutWrap: true,
      },
    },
  },
]

export const allPagesConfig: { [key: string]: ConfigProps } = {
  ...pagesConfig,
  ...Object.fromEntries(pageGroupsConfig.flatMap(({ children }) => Object.entries(children))),
}

export interface GroupChildRouteProps extends CommonProps {
  readonly path: string
  readonly element: React.JSX.Element
}

export interface RouteGroupProps {
  readonly layout: GroupLayout
  readonly children: GroupChildRouteProps[]
}

export const pageGroups: RouteGroupProps[] = pageGroupsConfig.map(({ layout, children }) => ({
  layout,
  children: Object.entries(children).flatMap(
    ([
      path,
      { component: Comp, componentProps, withButton = true, withoutWrap = false, ...rest },
    ]) => {
      const base = {
        ...rest,
        path: `/${path}`,
        element: <Comp {...componentProps} screenId={componentProps?.screenId ?? path} />,
        withButton,
        withoutWrap,
        insideGroup: true,
      } satisfies GroupChildRouteProps

      return [
        base,
        {
          ...base,
          path: `/:locale/${path}`,
          element: <LocalePathGate>{base.element}</LocalePathGate>,
        },
      ]
    },
  ),
}))
